import { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import { Sidebar } from "@/components/Sidebar";
import { CommandPalette } from "@/components/CommandPalette";
import { ClockProvider } from "@/components/ClockProvider";
import { TooltipProvider } from "@/components/ui/Tooltip";
import { Skeleton } from "@/components/ui/Skeleton";
import { ConsoleSocketProvider } from "@/hooks/useConsoleSocket";
import { useWhoami } from "@/hooks/useWhoami";
import { setUnauthorizedHandler } from "@/lib/api";
import { S } from "@/lib/strings";

import { Login } from "@/pages/Login";
import { Overview } from "@/pages/Overview";
import { Devices } from "@/pages/Devices";
import { DeviceDetail } from "@/pages/DeviceDetail";
import { Tasks } from "@/pages/Tasks";
import { TaskEditor } from "@/pages/TaskEditor";
import { Dispatch } from "@/pages/Dispatch";
import { Runs } from "@/pages/Runs";
import { RunDetail } from "@/pages/RunDetail";
import { FleetSettings } from "@/pages/FleetSettings";
import { NotFound, Phase2Page } from "@/pages/Phase2";
import { Data } from "@/pages/Data";

export function App() {
  return (
    <BrowserRouter>
      <TooltipProvider>
        <ClockProvider>
          <Toaster
            position="bottom-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "var(--color-panel)",
                color: "var(--color-fg)",
                border: "1px solid var(--color-border)",
              },
            }}
          />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<Shell />}>
              <Route path="/" element={<Navigate to="/overview" replace />} />
              <Route path="/overview" element={<Overview />} />

              <Route path="/devices" element={<Devices />} />
              <Route path="/devices/:id" element={<DeviceDetail />} />

              <Route path="/tasks" element={<Tasks />} />
              <Route path="/tasks/new" element={<TaskEditor />} />
              <Route path="/tasks/:id" element={<TaskEditor />} />

              <Route path="/dispatch" element={<Dispatch />} />

              <Route path="/runs" element={<Runs />} />
              <Route path="/runs/:id" element={<RunDetail />} />

              <Route path="/settings/fleet" element={<FleetSettings />} />

              <Route path="/data" element={<Data />} />

              {/* Phase 2 — routed so links resolve, honest about being empty. */}
              <Route
                path="/alerts"
                element={<Phase2Page title={S.navAlerts} body={S.phase2Alerts} />}
              />
              <Route
                path="/audit"
                element={<Phase2Page title={S.navAudit} body={S.phase2Audit} />}
              />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </ClockProvider>
      </TooltipProvider>
    </BrowserRouter>
  );
}

/**
 * The authenticated shell.
 *
 * The console socket lives here rather than at the root so the login page holds
 * no socket: an unauthenticated upgrade would be refused, and a refused upgrade
 * would sit in the backoff loop hammering the server with a cookie it does not
 * have.
 */
function Shell() {
  const { isPending, isKnown, isError, error } = useWhoami();
  const navigate = useNavigate();
  const location = useLocation();

  // Any 401 from anywhere lands on /login with a `next`, in-router.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?next=${next}`, { replace: true });
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate, location.pathname, location.search]);

  if (isPending) return <ShellSkeleton />;

  if (isError && error?.status === 401) {
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  // The server answered something other than 401 — a 500, a dead proxy. Render
  // the shell anyway so the operator can see the error card each page puts up,
  // but with no identity every Guarded control stays closed.
  return (
    <ConsoleSocketProvider>
      <CommandPalette />
      <div className="grid min-h-screen grid-cols-1 bg-[var(--color-canvas)] md:grid-cols-[220px_1fr]">
        <Sidebar />
        <main className="min-w-0 overflow-x-auto p-6 md:p-8">
          {!isKnown && isError && (
            <div className="mb-4 rounded-lg border border-[rgba(239,68,68,0.2)] bg-[rgba(239,68,68,0.04)] px-3 py-2 text-xs text-[#fca5a5]">
              {S.permissionDenied}：無法確認你的身分，所有操作已停用。{error?.message}
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </ConsoleSocketProvider>
  );
}

function ShellSkeleton() {
  return (
    <div className="grid min-h-screen grid-cols-1 bg-[var(--color-canvas)] md:grid-cols-[220px_1fr]">
      <div className="hidden flex-col gap-2 border-r border-[var(--color-border-subtle)] bg-[var(--color-panel)] p-3 md:flex">
        <Skeleton className="mb-3 h-7 w-32" />
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-7 w-full" />
        ))}
      </div>
      <main className="p-6 md:p-8">
        <Skeleton className="mb-6 h-8 w-48" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-[92px]" />
          ))}
        </div>
      </main>
    </div>
  );
}
